// La espera del plan de una marca nueva después de pagarlo, en "Tu nueva marca"
// (ADR-2026-10-29-espera-del-plan-de-marca-sin-trabar).
//
// El alta del plan la aplica SÓLO el webhook de pago. `ElegirPlanDeMarca` ya espera ~30 s;
// si al volver al tablero la billetera todavía no tiene plan, la pantalla no se queda con un
// error y un botón que no hace nada: confirma el pago sola (cada `cadaMs`, hasta `intentos`)
// y, si el plan no aparece, deja ir al estudio con el borrador intacto (el guard del shell lo
// activa en una visita posterior, cuando el plan ya está) o volver a verificar.

/** `confirmando`: se está mirando la billetera. `sin_confirmar`: la espera terminó sin plan. */
export type FaseDeLaEspera = 'confirmando' | 'sin_confirmar';

/** Cada 3 s durante ~2 minutos. */
/** `lecturaMaxMs`: tope de CADA lectura; una que se cuelga cuenta como "todavía no" (review Greptile #220). */
export const ESPERA_DEL_PLAN = { intentos: 40, cadaMs: 3_000, lecturaMaxMs: 10_000 } as const;

export const TEXTO_CONFIRMANDO = 'Estamos confirmando el pago de la marca. Esto puede demorar unos segundos.';
export const TEXTO_SIN_CONFIRMAR =
  'El pago todavía no se confirmó. Podés seguir usando Atlas: la marca se activa sola apenas se acredite.';

export interface VistaDeLaEspera {
  /** El aviso de la barra. */
  texto: string;
  /** El aviso lleva un spinner al lado (se está confirmando). */
  conSpinner: boolean;
  /** El botón principal de la barra. `accion: null` = deshabilitado, mostrando que trabaja. */
  principal: { texto: string; accion: 'ir_al_estudio' | null };
  /** La acción secundaria (un enlace), si hay. */
  secundaria: { texto: string; accion: 'volver_a_verificar' } | null;
}

/**
 * Qué muestra la barra según la fase. `error` es un fallo al ACTIVAR la marca cuando el plan
 * ya apareció (la escritura de la base): se muestra en lugar del texto de "sin confirmar",
 * con las mismas salidas. Nunca hay una acción que descarte el borrador: el pago pudo haber
 * entrado. Pura.
 */
export function vistaDeLaEspera(fase: FaseDeLaEspera, error: string | null = null): VistaDeLaEspera {
  if (fase === 'confirmando') {
    return {
      texto: TEXTO_CONFIRMANDO,
      conSpinner: true,
      principal: { texto: 'Confirmando…', accion: null },
      secundaria: null,
    };
  }
  return {
    texto: error ?? TEXTO_SIN_CONFIRMAR,
    conSpinner: false,
    principal: { texto: 'Ir al estudio', accion: 'ir_al_estudio' },
    secundaria: { texto: 'Volver a verificar', accion: 'volver_a_verificar' },
  };
}

/**
 * Mira `tienePlan` hasta `intentos` veces, con `cadaMs` entre una y otra. `true` apenas lo
 * ve; `false` si se agotaron los intentos o si `sigue()` dejó de ser cierto (la pantalla se
 * desmontó o arrancó otra espera): en ese caso no se hace ni una lectura más. Un error de
 * lectura cuenta como "todavía no", y también una que tarda más de `lecturaMaxMs`: sin ese tope, una
 * consulta colgada dejaba la barra girando para siempre sin llegar a "sin confirmar" (review
 * Greptile #220). `dormir` se inyecta en los tests.
 */
export async function esperarHastaQueTengaPlan(p: {
  tienePlan: () => Promise<boolean>;
  sigue: () => boolean;
  intentos?: number;
  cadaMs?: number;
  lecturaMaxMs?: number;
  dormir?: (ms: number) => Promise<void>;
}): Promise<boolean> {
  const lecturaMaxMs = p.lecturaMaxMs ?? ESPERA_DEL_PLAN.lecturaMaxMs;
  const intentos = p.intentos ?? ESPERA_DEL_PLAN.intentos;
  const cadaMs = p.cadaMs ?? ESPERA_DEL_PLAN.cadaMs;
  const dormir = p.dormir ?? ((ms: number) => new Promise<void>((res) => setTimeout(res, ms)));
  for (let i = 0; i < intentos; i++) {
    if (i > 0) await dormir(cadaMs);
    if (!p.sigue()) return false;
    const tiene = await conTope(p.tienePlan(), lecturaMaxMs);
    if (!p.sigue()) return false;
    if (tiene) return true;
  }
  return false;
}

/** La lectura, o `false` si falla o no contesta en `ms` (el timer se limpia al terminar). */
function conTope(lectura: Promise<boolean>, ms: number): Promise<boolean> {
  let t: ReturnType<typeof setTimeout> | undefined;
  const tope = new Promise<boolean>((res) => {
    t = setTimeout(() => res(false), ms);
  });
  return Promise.race([lectura.catch(() => false), tope]).finally(() => clearTimeout(t));
}
